// localPrint.js — a till's own direct connection to its branch's local
// print hub (see cmd/printagent/local_server.go, running on a machine
// somewhere on the same LAN). Deliberately independent of api.js's own
// request() helper: every call here talks STRAIGHT to that LAN machine,
// never through this app's own cloud server — the entire point during an
// offline stretch (see this feature's own plan doc, "Offline Receipt
// Printing via a Local Print Hub + Per-Branch Auto-Print Toggle").
//
// The hub's URL + bearer token are configured ONCE per till, from
// LocalPrintSetup.js, and persisted in THIS BROWSER's own localStorage —
// deliberately per-till, per-browser state rather than anything server-side:
// a till only ever needs to remember its own LAN hub, and that value must
// stay readable with zero connectivity at all, which rules out ever
// fetching it from this app's own server.

const STORAGE_KEY = "posflect_local_print_hub";
const REQUEST_TIMEOUT_MS = 4000;

function normalizeUrl(url) {
  return (url || "").trim().replace(/\/+$/, "");
}

// getLocalHubConfig reads this till's saved { url, token } — null when
// nothing's been configured yet, or when whatever's stored is malformed
// (never throws either way, since every caller needs to keep working with
// or without a hub configured).
export function getLocalHubConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.url || !parsed?.token) return null;
    return parsed;
  } catch (err) {
    return null;
  }
}

export function saveLocalHubConfig(url, token) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ url: normalizeUrl(url), token: (token || "").trim() }));
    return true;
  } catch (err) {
    return false;
  }
}

export function clearLocalHubConfig() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    // best-effort — a browser blocking storage access entirely just means
    // there was nothing meaningfully "saved" in the first place
  }
}

// postToHub is the one shared HTTP call both attemptLocalPrint and
// printTestReceipt below make — same endpoint
// (cmd/printagent/local_server.go's own POST /local/print-receipt), same
// short timeout (a till mid-sale can't afford to hang waiting on a LAN
// machine that's powered off), same "never throws" contract.
async function postToHub(url, token, body) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${normalizeUrl(url)}/local/print-receipt`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || `local hub returned HTTP ${res.status}` };
    }
    return { success: true };
  } catch (err) {
    return { success: false, error: err?.name === "AbortError" ? "timed out reaching the local print hub" : (err?.message || "could not reach the local print hub") };
  } finally {
    clearTimeout(timeoutId);
  }
}

// attemptLocalPrint POSTs a receipt snapshot straight to this till's own
// configured hub — see cmd/printagent/local_server.go's own
// localPrintReceiptRequest for the exact shape it expects (Checkout.js's
// own buildLocalReceiptSnapshot helper builds it). NEVER throws: every
// failure mode (unconfigured, unreachable, disabled on the hub, wrong
// token, printer offline) just resolves to { success: false, error } so
// Checkout.js's own offline submit path can fold the result straight into
// already_printed_offline and keep going regardless — a local print is a
// bonus on top of the sale that's already been queued, never a condition
// for completing it.
export async function attemptLocalPrint(snapshot) {
  const config = getLocalHubConfig();
  if (!config) {
    return { success: false, error: "no local print hub configured on this till" };
  }
  return postToHub(config.url, config.token, snapshot);
}

// printTestReceipt is LocalPrintSetup.js's own "Print Test Receipt" button —
// same endpoint and shape as attemptLocalPrint, but built from an
// obviously-fake sample sale, and (unlike attemptLocalPrint) taking the
// url/token straight from the setup form rather than localStorage, so a
// till can test BEFORE saving. This deliberately prints a real slip on the
// branch's actual printer — the same "wire it up, then look at the paper
// that comes out" confirmation any printer setup screen gives, simpler and
// more trustworthy than a silent connectivity probe that can't rule out a
// jammed or disconnected printer.
export async function printTestReceipt(url, token) {
  return postToHub(url, token, {
    client_request_id: "test-" + Date.now(),
    created_at: new Date().toISOString(),
    cashier_username: "test",
    cashier_first_name: "Local Hub",
    cashier_last_name: "Test",
    items: [
      { product_name: "Test Item", unit_name: "", quantity: 1, display_quantity: 1, unit_price: 10, line_total: 10 },
    ],
    service_items: [],
    payments: [{ method: "cash", amount: 10, received_amount: 10, change_amount: 0 }],
    subtotal: 10,
    discount_total: 0,
    tax_total: 0,
    grand_total: 10,
  });
}
